import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import SimpleTable from "@/components/SimpleTable/SimpleTable";
import type { Column } from "@/types/common";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { UpcomingEvent } from "@/services/api/dashboard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getUpcomingEvents } from "@/store/slices/dashboardSlice";
import { EVENT_TYPE_LABELS, formatEventDate, GATHERING_STATUS_TONE, getPublishingLabel } from "@/pages/Gatherings/lib/eventFormat";
import routes from "@/routes/routes";
import styles from "./UpcomingGatherings.module.scss";
import { TableCellTooltip } from "@/components/Tooltip";

// This preview card only ever shows a handful, unlike the full Gatherings
// page's grid — matches the original Figma mock's row count.
const MAX_ROWS = 5;

const FETCH_PARAMS = { pageNo: 1, perPage: MAX_ROWS };

const COLUMNS: Column<UpcomingEvent>[] = [
  { name: "Gathering", width: "40%", cell: (row) => <TableCellTooltip value={row.name} /> },
  { name: "Type", selector: (row) => EVENT_TYPE_LABELS[row.type] ?? row.type },
  // `UpcomingEvent.cities` comes back populated directly on this endpoint
  // (unlike the full `Event` type's own `cities`, confirmed always `null` —
  // see services/api/dashboard.ts), so no `locations` fallback is needed.
  { name: "City", selector: (row) => row.cities[0] || "—" },
  { name: "Date", selector: (row) => formatEventDate(row.date) },
  { name: "Capacity", selector: (row) => row.capacity || "—" },
  {
    name: "Status",
    cell: (row) => {
      if (!row.eventPublishingStatus) return <span className={styles.statusFallback}>—</span>;
      const label = getPublishingLabel(row.eventPublishingStatus);
      return <StatusBadge label={label.toUpperCase()} tone={GATHERING_STATUS_TONE[label] ?? "neutral"} />;
    },
  },
];

const UpcomingGatherings = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const events = useAppSelector((state) => state.dashboard.upcomingEvents);

  useEffect(() => {
    dispatch(getUpcomingEvents(FETCH_PARAMS));
  }, [dispatch]);

  // The endpoint's own sample wasn't sorted soonest-first (see
  // services/api/dashboard.ts), so this table still sorts client-side.
  const upcoming = useMemo(
    () => [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, MAX_ROWS),
    [events],
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h2 className={styles.title}>Upcoming Gatherings</h2>
        <button
          type="button"
          className={styles.viewAll}
          onClick={() => navigate(routes.privateRoutes.GATHERINGS.path)}
        >
          View all
          <ArrowIcon className={styles.viewAllIcon} />
        </button>
      </div>

      <SimpleTable<UpcomingEvent>
        columns={COLUMNS}
        data={upcoming}
        // No unique id on this endpoint's items (see UpcomingEvent's own
        // comment in services/api/dashboard.ts) — name+date is the closest
        // available stand-in for a row key.
        getRowKey={(row) => `${row.name}-${row.date}`}
        emptyMessage="No upcoming gatherings"
      />
    </div>
  );
};

export default UpcomingGatherings;
